import Container from '../../../atoms/Container';
import { ExerciseAction, IExercise } from '../../../../models/exercise';
import { IntersectionIcon } from 'morpheus-ui';
import { Fragment, ReactNode, useState } from 'react';
import ContentLoader from 'react-content-loader';
import { useExamHook } from '../../../../hooks/exam';
import ExamNavigation from '../../../molecules/Exam/Navigation';
import { AnswerSheet } from '../../../organism/AnswerSheet';
import { Modal } from '../../Modal';
import { Typography } from '../../../molecules/Typography';
import { Button } from '../../../atoms/Button';
import { ExamExerciseList } from '../../../organism/Exam/ExerciseList';
import { usePrintComponentStore } from '../../../organism/PrintComponent/store/usePrintComponentStore';
import NewPrintComponent from '../../../organism/PrintComponent/newPrintComponent';
import SimuladoPrintModal from '../../../organism/PrintComponent/SimuladoPrintModal';
import ExercisePaper from '../../PrintPaper/exercises';

interface ExamProps {
  exercises: IExercise[];
  isLoading?: boolean;
  timeLeft?: number;
  type?: 'default' | 'enem';
  /** Sem prazo: desliga o encerramento automatico quando o tempo chega a zero. */
  isPractice?: boolean;
  isMegaSimulation?: boolean;
  /** Dia de prova no formato de dois dias. */
  day?: 1 | 2 | null;
  /** Caderno diagramado (sem gabarito) daquele dia, cadastrado no admin. */
  provaPdfUrl?: string | null;
  examName?: string;
  /** Relogio proprio exibido no rodape no lugar da contagem de `timeLeft`. */
  timer?: ReactNode;
  onExerciseChange: (exercise: IExercise, action?: ExerciseAction) => void;
  onFinishExam: (isTimeExpired?: boolean) => void;
}

const Exam = ({
  exercises,
  timeLeft,
  isLoading = false,
  type = 'default',
  isPractice = false,
  isMegaSimulation = false,
  day = null,
  provaPdfUrl = null,
  examName,
  timer,
  onExerciseChange,
  onFinishExam,
}: ExamProps) => {
  const [selectedQuestion, setSelectedQuestion] = useState<string>();
  // Fallback para simulados sem PDF diagramado cadastrado: cai na impressão
  // gerada a partir da página, que é o que existia antes.
  const [useGeneratedPrint, setUseGeneratedPrint] = useState(false);
  const { setData } = usePrintComponentStore();

  const {
    isOpenPrint,
    isTimeExpired,
    isOpenAnswerCard,
    isOpenFinishConfirmation,
    confirmEndOfExam,
    setIsOpenPrint,
    setIsOpenAnswerCard,
    setIsOpenFinishConfirmation,
  } = useExamHook();

  const totalExercises = exercises.length;
  const exercisesDone = exercises.filter(
    (exercise) => exercise.hasAlternativeSelected
  ).length;

  return (
    <Container>
      {!exercises ? (
        <Fragment>
          <ContentLoader
            backgroundColor={'#a7adba'}
            foregroundColor={'#c0c5ce'}
            viewBox='0 0 500 30'
          >
            <rect x='0' y='2' rx='2' ry='2' width='100' height='15' />
          </ContentLoader>
          <ContentLoader
            backgroundColor={'#a7adba'}
            foregroundColor={'#c0c5ce'}
            viewBox='0 0 500 80'
          >
            <rect x='0' y='2' rx='2' ry='2' width='100%' height='70' />
          </ContentLoader>
          {Array.from({ length: 5 }, (_, index) => (
            <ContentLoader
              key={`${index}_loader`}
              backgroundColor={'#a7adba'}
              foregroundColor={'#c0c5ce'}
              viewBox='0 0 500 24'
            >
              <rect x='0' y='2' rx='2' ry='2' width='100%' height='20' />
            </ContentLoader>
          ))}
        </Fragment>
      ) : (
        <div className='flex flex-col gap-xxl pb-20'>
          {/*
            Simulado Enem imprime o caderno diagramado do dia. O gabarito não
            entra aqui de propósito: entregá-lo com a prova em andamento seria
            dar as respostas. Ele aparece na página de resultados.
          */}
          {type === 'enem' ? (
            <>
              <SimuladoPrintModal
                isOpen={isOpenPrint && !useGeneratedPrint}
                onClose={() => setIsOpenPrint(false)}
                title='Imprimir prova'
                subtitle={examName}
                items={[
                  {
                    id: 'prova',
                    label: day ? `Prova — ${day}º dia` : 'Prova',
                    description: 'Caderno de questões no modelo Enem, sem gabarito.',
                    url: provaPdfUrl,
                    filename: day
                      ? `prova-dia-${day}.pdf`
                      : 'prova.pdf',
                    unavailableReason:
                      'O caderno diagramado deste simulado ainda não foi publicado.',
                  },
                ]}
                onFallbackPrint={() => setUseGeneratedPrint(true)}
              />
              <NewPrintComponent
                type='practice'
                isOpen={isOpenPrint && useGeneratedPrint}
                onClose={() => {
                  setIsOpenPrint(false);
                  setUseGeneratedPrint(false);
                }}
                onCancel={() => setUseGeneratedPrint(false)}
                title='Imprimir prova'
                subtitle={examName}
              >
                <ExercisePaper
                  cols='split'
                  size='default'
                  color='interdisciplinar'
                  iconType='svg'
                  icon={IntersectionIcon}
                  title='Exercícios'
                  module='interdisciplinar'
                />
              </NewPrintComponent>
            </>
          ) : (
            <NewPrintComponent
              type='practice'
              isOpen={isOpenPrint}
              onClose={() => setIsOpenPrint(false)}
              title={isMegaSimulation ? 'Imprimir' : 'Imprimir SSR'}
              subtitle='interdisciplinar'
            >
              <ExercisePaper
                cols='split'
                size='default'
                color='interdisciplinar'
                iconType='svg'
                icon={IntersectionIcon}
                title='Exercícios'
                module='interdisciplinar'
              />
            </NewPrintComponent>
          )}

          <div className='flex flex-col items-center justify-between gap-xxl'>
            <ExamExerciseList
              data={exercises}
              isLoading={isLoading}
              orderBy={type === 'enem' ? 'order' : 'area'}
              onExerciseChange={(exercise, action) =>
                onExerciseChange(exercise, action)
              }
              selectedId={selectedQuestion}
              clearSelected={() => {
                setIsOpenAnswerCard(false);
                setSelectedQuestion(undefined);
              }}
            />
          </div>
        </div>
      )}
      <AnswerSheet
        isOpen={isOpenAnswerCard}
        answers={exercises}
        orderBy={type === 'enem' ? 'order' : 'area'}
        buttonText='Finalizar simulado'
        onClose={() => setIsOpenAnswerCard(false)}
        onClick={() => confirmEndOfExam(isTimeExpired)}
        onSelect={(id: string) => setSelectedQuestion(id)}
      />
      <Modal
        isOpen={isOpenFinishConfirmation}
        onClose={() => setIsOpenFinishConfirmation(false)}
        position='center'
        className='w-2/5 p-0'
      >
        <div className='flex flex-col gap-xl'>
          {isTimeExpired ? (
            <Typography>Tempo esgotado! Calculando os resultados...</Typography>
          ) : exercisesDone < totalExercises ? (
            <Typography>
              <strong>
                Há {totalExercises - exercisesDone} questões sem resposta.
              </strong>{' '}
              Se você finalizar o simulado, essas questões serão consideradas
              erradas.
            </Typography>
          ) : (
            <Typography>
              Tem certeza que deseja finalizar o simulado?
            </Typography>
          )}
          <div className='flex flex-row items-center justify-end gap-sm'>
            {isTimeExpired ? (
              <Button
                variant='text'
                loading={isLoading}
                disabled={isLoading}
                onClick={() => onFinishExam(true)}
              >
                Ok
              </Button>
            ) : (
              <>
                <Button
                  variant='text'
                  disabled={isLoading}
                  onClick={() => setIsOpenFinishConfirmation(false)}
                >
                  Voltar
                </Button>
                <Button
                  variant='text'
                  loading={isLoading}
                  disabled={isLoading}
                  onClick={() => onFinishExam(false)}
                >
                  Finalizar simulado
                </Button>
              </>
            )}
          </div>
        </div>
      </Modal>
      <ExamNavigation
        totalExercises={totalExercises}
        exercisesDone={exercisesDone}
        timeLeft={timeLeft}
        isPractice={isPractice}
        isLoading={isLoading}
        timer={timer}
        onAnswerCardClick={() => setIsOpenAnswerCard(true)}
        onFinish={(isTimeExpired?: boolean) => confirmEndOfExam(isTimeExpired)}
        onPrint={() => {
          setData(exercises);
          setIsOpenPrint(true);
        }}
      />
    </Container>
  );
};

export default Exam;
